import type { SlotGridCell } from '../ui';
import type { LetterheadSlotKey, ReportLetterhead } from '../../lib/agentShared/formSchema';

// The letterhead panel's grid TOPOLOGY (story 4-88b) — where each of 4-88a's
// fixed 7 named slots sits on a 2D grid, so `SlotGridPicker`'s spatial
// adjacency (`findAdjacentSlot`) has real positions to scan between. One
// module, imported by BOTH `ReportDesigner.tsx` (the real feature) and the
// `/app/design` gallery, so the demo and the feature can never drift onto
// two different topologies.
//
// ── Where this topology comes from ──────────────────────────────────────
// Not invented for this story: it is the layout 4-88a's OWN renderers
// already committed to. `pdfExport.service.js#buildReportHtml`'s
// `.report-letterhead-logos` is a `display:flex` row of exactly
// logoLeft/logoCenter/logoRight (row 1), followed by a centered `<p
// class="report-letterhead-title">` (row 2) then `<p class="report-
// letterhead-address">` (row 3) — three stacked, center-column blocks, not a
// fourth column. `buildFooterTemplate`'s footer is a SEPARATE two-slot row
// (`justify-content:space-between`): footerText on the left, footerPageNumber
// on the right. This grid is that exact shape, so "move logoLeft down" or
// "move footerText right" lands where a reader would already expect from
// the rendered output, not an arbitrary picker layout invented independent
// of it:
//
//   [logoLeft]     [logoCenter]      [logoRight]
//       .             [title]            .
//       .          [addressBlock]        .
//   [footerText]       .           [footerPageNumber]
//
// Empty grid positions (the dots) are not slots — `findAdjacentSlot` SKIPS
// them and lands on the next occupied cell in that direction (e.g. "up" from
// `footerText` reaches `logoLeft`, not an empty row-3/row-2 cell), which is
// what makes every one of the 7 slots reachable from every other one instead
// of some being isolated dead ends.
export const LETTERHEAD_GRID_ROWS = 4;
export const LETTERHEAD_GRID_COLS = 3;

export const LETTERHEAD_SLOT_LABELS: Record<LetterheadSlotKey, string> = {
  logoLeft: 'Logo — left',
  logoCenter: 'Logo — center',
  logoRight: 'Logo — right',
  title: 'Title',
  addressBlock: 'Address block',
  footerText: 'Footer text',
  footerPageNumber: 'Page number',
};

export const LETTERHEAD_GRID_CELLS: (SlotGridCell & { id: LetterheadSlotKey })[] = [
  { id: 'logoLeft', row: 1, col: 1, label: LETTERHEAD_SLOT_LABELS.logoLeft },
  { id: 'logoCenter', row: 1, col: 2, label: LETTERHEAD_SLOT_LABELS.logoCenter },
  { id: 'logoRight', row: 1, col: 3, label: LETTERHEAD_SLOT_LABELS.logoRight },
  { id: 'title', row: 2, col: 2, label: LETTERHEAD_SLOT_LABELS.title },
  { id: 'addressBlock', row: 3, col: 2, label: LETTERHEAD_SLOT_LABELS.addressBlock },
  { id: 'footerText', row: 4, col: 1, label: LETTERHEAD_SLOT_LABELS.footerText },
  { id: 'footerPageNumber', row: 4, col: 3, label: LETTERHEAD_SLOT_LABELS.footerPageNumber },
];

/**
 * Relocate — SWAPS the two slots' full `{enabled, content}` values (not a
 * one-way overwrite). A plain overwrite would silently destroy whatever the
 * target slot already held; a swap is reversible (pressing the opposite
 * direction immediately after undoes it) and matches how every other
 * "move" control in this design system behaves (`OrderedListField`'s
 * up/down splices, it does not overwrite-and-drop). The caller is
 * responsible for also moving `activeId` to `toKey` so the active slot
 * "follows" its content to the new position — that is a selection concern,
 * not a data concern, so it lives in the caller (`ReportDesigner.tsx`), not
 * here.
 */
export function relocateLetterheadSlot(
  letterhead: ReportLetterhead,
  fromKey: LetterheadSlotKey,
  toKey: LetterheadSlotKey,
): ReportLetterhead {
  return {
    ...letterhead,
    [fromKey]: letterhead[toKey],
    [toKey]: letterhead[fromKey],
  };
}
